import { ReactNode, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  CheckSquare,
  MessageSquare,
  FileText,
  LogOut,
  User as UserIcon,
  Wallet,
  Map,
  Palette,
  Upload,
} from "lucide-react";

import { useTasks, useRealtimeTasks } from "@/hooks/use-tasks";
import { useRequests, useRealtimeRequests } from "@/hooks/use-requests";
import { useUnreadCount, markSeen } from "@/hooks/use-unread-badges";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/Logo";
import { usePageTransition } from "@/components/system";
import { QuickQuestion } from "@/components/portal/QuickQuestion";
import { PortalBottomNav } from "@/components/portal/PortalBottomNav";
import { usePortalClient } from "@/components/portal/use-portal-client";
import { initials } from "@/lib/format";
import { cn } from "@/lib/utils";

const tabs = [
  { to: "/portal", end: true, label: "Home", icon: LayoutDashboard },
  { to: "/portal/tasks", label: "Lavori", icon: CheckSquare },
  { to: "/portal/roadmap", label: "Percorso", icon: Map },
  { to: "/portal/finance", label: "Rendiconto", icon: Wallet },
  { to: "/portal/requests", label: "Richieste", icon: MessageSquare },
  { to: "/portal/reports", label: "Report", icon: FileText },
  { to: "/portal/uploads", label: "File", icon: Upload },
  { to: "/portal/brand", label: "Brand", icon: Palette },
];

export function PortalLayout({ children }: { children: ReactNode }) {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const page = usePageTransition();
  const { name: clientName, accent, hasBrand } = usePortalClient();

  useRealtimeTasks();
  useRealtimeRequests();
  const { data: tasks } = useTasks();
  const { data: requests } = useRequests({ onlyMine: true });
  const unreadTasks = useUnreadCount("portal_tasks", tasks);
  const unreadRequests = useUnreadCount("portal_requests", requests);

  useEffect(() => {
    if (location.pathname === "/portal/tasks") markSeen("portal_tasks");
    if (location.pathname === "/portal/requests") markSeen("portal_requests");
  }, [location.pathname]);

  const unreadByPath: Record<string, number> = {
    "/portal/tasks": unreadTasks,
    "/portal/requests": unreadRequests,
  };

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-screen min-w-0 max-w-full flex-col overflow-x-hidden bg-background">
      {/* §8: prima cosa raggiungibile da tastiera. */}
      <a
        href="#portale-contenuto"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:border focus:border-primary/40 focus:bg-card-2 focus:px-4 focus:py-2 focus:text-sm focus:text-foreground"
      >
        Vai al contenuto
      </a>

      {/* pt-safe: in standalone su iOS la webview parte sotto la status bar */}
      <header className="pt-safe sticky top-0 z-40 border-b border-white/[0.06] bg-sidebar/85 backdrop-blur-xl">
        {/* Filo d'oro: la firma della casa anche sulla testata (§9). */}
        <span aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-brand opacity-60" />

        <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 px-3 py-2.5 sm:px-4 sm:py-3 md:px-6">
          <Link
            to="/portal"
            className="flex min-w-0 items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Logo size={32} className="shrink-0" />
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="truncate font-display text-xs font-bold tracking-[0.16em] text-gold sm:text-sm">
                MACHIAVELLI
              </span>
              <span className="hidden truncate font-serif text-[11px] italic text-muted-foreground sm:block">
                {clientName ? `Il quartier generale di ${clientName}` : "La tua area riservata"}
              </span>
            </span>
          </Link>

          <div className="flex shrink-0 items-center gap-2">
            <div className="hidden text-right text-xs leading-tight md:block">
              <div className="max-w-[16rem] truncate font-medium">
                {profile?.full_name ?? profile?.email ?? ""}
              </div>
              <div className="font-display text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Cliente
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-lg"
                  className="rounded-full"
                  aria-label="Il tuo account"
                  title="Il tuo account"
                >
                  {/* Medaglione con anello nel colore del cliente (§9). */}
                  <span
                    aria-hidden
                    className="flex h-8 w-8 items-center justify-center rounded-full transition-shadow duration-base ease-palazzo"
                    style={{
                      boxShadow: `0 0 0 2px color-mix(in srgb, ${accent} ${hasBrand ? 45 : 30}%, transparent)`,
                    }}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={profile?.avatar_url ?? undefined}
                        alt={profile?.full_name ?? ""}
                      />
                      <AvatarFallback className="bg-gradient-brand font-display font-semibold text-primary-foreground">
                        {initials(profile?.full_name ?? profile?.email)}
                      </AvatarFallback>
                    </Avatar>
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">{profile?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/portal/profile")}>
                  <UserIcon className="mr-2 h-4 w-4" aria-hidden />
                  Profilo e password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" aria-hidden />
                  Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Navigazione: scorrevole su mobile, fissa su desktop. */}
        <nav
          aria-label="Sezioni del portale"
          className="mx-auto flex w-full max-w-5xl gap-1 overflow-x-auto px-3 pb-2 sm:px-4 md:px-6 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {tabs.map((t) => {
            const unread = unreadByPath[t.to] ?? 0;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                className={({ isActive }) =>
                  cn(
                    "relative flex h-10 shrink-0 items-center gap-1.5 rounded-md px-3 text-xs font-medium",
                    "transition-colors duration-fast ease-palazzo",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isActive
                      ? "text-primary"
                      : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        aria-hidden
                        layoutId="portal-tab-active"
                        className="absolute inset-0 -z-10 rounded-md bg-primary/[0.10] shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.22)]"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                    <t.icon className="h-3.5 w-3.5" aria-hidden />
                    {t.label}
                    {unread > 0 && (
                      <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 font-display text-[10px] font-bold tabular-nums text-primary-foreground">
                        {unread > 9 ? "9+" : unread}
                        <span className="sr-only"> aggiornamenti da vedere</span>
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
      </header>

      <QuickQuestion />
      <PortalBottomNav />

      <main
        id="portale-contenuto"
        tabIndex={-1}
        className="mx-auto w-full min-w-0 max-w-5xl flex-1 px-3 py-4 sm:px-4 md:px-6 md:py-8"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            variants={page}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="min-w-0 max-w-full"
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer
        className="mx-auto w-full max-w-5xl px-3 pt-2 sm:px-4 md:px-6"
        style={{ paddingBottom: "var(--page-pb)" }}
      >
        <p className="border-t border-white/[0.06] pt-4 text-center font-serif text-xs italic text-muted-foreground">
          Machiavelli Solutions — ogni lavoro, sempre sotto i tuoi occhi.
        </p>
      </footer>
    </div>
  );
}
